import { useCallback, useContext, useState } from 'react';
import { useQueryClient } from 'react-query';
import { StripeCardElement } from '@stripe/stripe-js';

import { CreditCardForm } from '~/components/modules/Forms/CreditCardForm';
import { AnalyticsEventNames } from '~/constants/AnalyticsEventMap';
import { queries } from '~/constants/queryKeys';
import { DepositContext } from '~/context/Deposit/DepositContext';
import { SentryHelper } from '~/helpers/Sentry/SentryHelper';
import { useNewCreditCard } from '~/hooks/payments/useNewCreditCard';
import { useTracking } from '~/hooks/tracking/useTracking';
import { ModalMultiStepProps } from '~/models/deposits/ModalMultiStepProps';

interface ConnectCreditCardProps extends ModalMultiStepProps {
    onPaymentLinked: () => void;
}

/** Connect a new credit card to a user's saved payment methods. */
export const ConnectCreditCard = ({ setStep, onPaymentLinked }: ConnectCreditCardProps) => {
    const { setDefaultPaymentSourceID } = useContext(DepositContext);
    const queryClient = useQueryClient();

    const { captureEvent } = useTracking();
    const { saveNewCreditCard, error, loading } = useNewCreditCard();

    const [stripeCardElement, setStripeCardElement] = useState<StripeCardElement | null | undefined>();

    const onCreditCardInfoEntered: React.ComponentProps<typeof CreditCardForm>['onCreditCardInfoEntered'] = useCallback(
        async (data) => {
            try {
                const paymentMethodID = await saveNewCreditCard(data?.name, stripeCardElement);
                if (paymentMethodID) {
                    captureEvent(AnalyticsEventNames.NewPaymentMethodAdded, { method: 'credit_card' });
                    setDefaultPaymentSourceID(paymentMethodID as string);

                    await queryClient.refetchQueries([queries.getSavedPaymentMethods]);
                    onPaymentLinked();
                }
            } catch (newCardError) {
                SentryHelper.captureException(newCardError, {
                    tags: { payment: 'credit_card' }
                });
                console.error(newCardError);
            }
        },
        [saveNewCreditCard, queryClient]
    );

    return (
        <CreditCardForm
            onCreditCardInfoEntered={onCreditCardInfoEntered}
            loading={loading}
            error={error}
            setStripeCardElement={setStripeCardElement}
            setStep={setStep}
        />
    );
};
